import { Component, AfterViewInit, ViewChild, ElementRef, OnChanges, SimpleChanges, input } from '@angular/core';
import {
  Chart,
  ChartConfiguration,
  registerables
} from 'chart.js';

Chart.register(...registerables);

@Component({
  selector: 'app-area-chart',
  templateUrl: './area-chart.component.html',
  styleUrls: ['./area-chart.component.scss']
})
export class AreaChartComponent implements AfterViewInit, OnChanges {
  @ViewChild('areaCanvas') areaCanvas!: ElementRef<HTMLCanvasElement>;
  chart!: Chart;

  readonly segments = input.required<{ label: string; value: number; }[]>();
  readonly label = input.required<string>();
  readonly onlyIntegers = input<boolean>(true);

  ngAfterViewInit(): void {
    this.initializeChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.chart) {
      let requiresUpdate = false;

      if (changes['segments']) {
        this.updateChartData();
        requiresUpdate = true;
      }

      if (changes['onlyIntegers']) {
        this.updateYAxisOptions();
        requiresUpdate = true;
      }

      if (requiresUpdate) {
        this.chart.update();
      }
    }
  }

  private initializeChart(): void {
    const canvas = this.areaCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Imposta le dimensioni del canvas al contenitore
    const parent = canvas.parentElement;
    if (parent) {
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
    }

    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height + 150);
    gradient.addColorStop(0, 'rgba(0, 143, 251, 0.7)');
    gradient.addColorStop(1, 'rgba(0, 143, 251, 0.1)');

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        labels: this.segments().map(item => item.label),
        datasets: [
          {
            label: this.label(),
            data: this.segments().map(item => item.value),
            fill: true,
            borderColor: '#008FFB',
            backgroundColor: gradient,
            tension: 0.4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                const value = context.parsed.y;
                return `${context.dataset.label}: ${Number.isInteger(value) ? value : value?.toFixed(2)}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false }
          },
          y: {
            beginAtZero: true,
            ticks: {
              maxTicksLimit: 6,
              callback: (value: number | string) => {
                if (!this.onlyIntegers()) return value;
                return Number.isInteger(+value) ? value : '';
              },
              stepSize: this.onlyIntegers() ? 1 : undefined
            }
          }
        }
      }
    };

    this.chart = new Chart(canvas, config);
  }

  private updateChartData(): void {
    this.chart.data.labels = this.segments().map(item => item.label);
    this.chart.data.datasets[0].data = this.segments().map(item => item.value);
    this.chart.data.datasets[0].label = this.label();
    this.chart.update();
  }

  private updateYAxisOptions(): void {
    if (!this.chart.options.scales) return;

    const yAxis = this.chart.options.scales['y'];
    if (!yAxis || typeof yAxis !== 'object') return;

    yAxis.ticks = {
      maxTicksLimit: 6,
      callback: (value: string | number) => {
        if (!this.onlyIntegers()) return value;
        return Number.isInteger(+value) ? value : '';
      },
      stepSize: this.onlyIntegers() ? 1 : undefined
    };
  }
}
